import { ReactElement } from 'react'
import { Box, CircularProgress, Tooltip, useTheme } from '@mui/material'
import SaveIcon from 'src/assets/icons/material-symbols/outlined/save.svg?react'

interface Props {
  isSaving: boolean
  /** The saved data is being (re)loaded; shown like saving when nothing is being saved. */
  isLoading?: boolean
  dirty: boolean
  /** Flush the pending autosave immediately. */
  onSaveNow: () => void
  'data-cy'?: string
}

/**
 * Compact autosave state: a spinner while saving or loading, otherwise a save icon that is green when
 * everything is saved and orange (clickable, saves immediately) while changes are pending.
 */
const AutosaveIndicator = ({
  isSaving,
  isLoading = false,
  dirty,
  onSaveNow,
  'data-cy': dataCy,
}: Props): ReactElement => {
  const theme = useTheme()

  if (isSaving || isLoading) {
    return (
      <Tooltip title={isSaving ? 'Speichert…' : 'Wird geladen…'} arrow disableInteractive>
        <Box
          component="span"
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 16,
            height: 16,
            color: theme.palette.text.secondary,
            flexShrink: 0,
          }}
          data-cy={dataCy}
        >
          <CircularProgress size={14} thickness={5} />
        </Box>
      </Tooltip>
    )
  }

  return (
    <Tooltip title={dirty ? 'Änderungen speichern' : 'Alles gespeichert'} arrow disableInteractive>
      <Box
        component="button"
        type="button"
        onClick={() => {
          if (dirty) onSaveNow()
        }}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 16,
          height: 16,
          border: 0,
          background: 'transparent',
          padding: 0,
          cursor: dirty ? 'pointer' : 'default',
          color: dirty ? theme.palette.warning.main : theme.palette.success.main,
          flexShrink: 0,
          '& svg': { fill: 'currentColor' },
          '&:hover': dirty ? { filter: 'brightness(0.92)' } : undefined,
        }}
        data-cy={dataCy}
      >
        <SaveIcon width={14} height={14} aria-hidden />
      </Box>
    </Tooltip>
  )
}

export default AutosaveIndicator
